import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "../i18n";

const FILTERS = ["All", "Rings", "High Jewellery", "Castings"];
const CAT_LABELS = {
  All: { en: "All", it: "Tutti" },
  Rings: { en: "Rings", it: "Anelli" },
  "High Jewellery": { en: "High Jewellery", it: "Alta Gioielleria" },
  Castings: { en: "Castings", it: "Fusioni" },
};

const PIECES = [
  { title: "Solitaire No. 4", cat: "Rings", img: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=900&auto=format&fit=crop", note: { en: "Platinum 950, four-prong setting", it: "Platino 950, incastonatura a quattro griffe" }, span: "row-span-2" },
  { title: "Trinity Stack", cat: "Rings", img: "https://images.unsplash.com/photo-1543294001-f7cd5d7fb516?q=80&w=900&auto=format&fit=crop", note: { en: "18K gold, pavé stack set", it: "Oro 18K, set di fedi pavé" }, span: "" },
  { title: "Baroque Strand", cat: "High Jewellery", img: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=900&auto=format&fit=crop", note: { en: "Hand-knotted pearls, gold clasp", it: "Perle annodate a mano, chiusura in oro" }, span: "" },
  { title: "Gilded Drops", cat: "High Jewellery", img: "https://images.unsplash.com/photo-1573408301185-9146fe634ad0?q=80&w=900&auto=format&fit=crop", note: { en: "Diamond-set link bracelets", it: "Bracciali a maglie con diamanti" }, span: "row-span-2" },
  { title: "Azure Pendant", cat: "High Jewellery", img: "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?q=80&w=900&auto=format&fit=crop", note: { en: "Bezel-set stone, 18K chain", it: "Pietra a castone, catena in oro 18K" }, span: "" },
  { title: "Aurum Collar", cat: "High Jewellery", img: "https://images.unsplash.com/photo-1602173574767-37ac01994b2a?q=80&w=900&auto=format&fit=crop", note: { en: "Cast links, high polish", it: "Maglie fuse, alta lucidatura" }, span: "" },
  { title: "Ember Bangle", cat: "High Jewellery", img: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?q=80&w=900&auto=format&fit=crop", note: { en: "Warm-toned stone pavé bangle", it: "Bangle pavé in tonalità calde" }, span: "" },
  { title: "Granulation Study", cat: "High Jewellery", img: "https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?q=80&w=900&auto=format&fit=crop", note: { en: "Etruscan revival technique", it: "Tecnica di granulazione etrusca" }, span: "" },
  { title: "Fresh from the Flask", cat: "Castings", img: "https://images.unsplash.com/photo-1609619742069-f5e18afeef17?q=80&w=900&auto=format&fit=crop", note: { en: "Raw casting, pre-finish", it: "Fusione grezza, prima della finitura" }, span: "" },
  { title: "At the Bench", cat: "Castings", img: "https://images.unsplash.com/photo-1599071338288-49173359e0fd?q=80&w=900&auto=format&fit=crop", note: { en: "Where every piece begins", it: "Dove ogni pezzo ha inizio" }, span: "" },
];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const { L } = useLang();
  const pieces = PIECES.filter((p) => filter === "All" || p.cat === filter);

  return (
    <section id="portfolio" data-testid="portfolio-gallery-section" className="mx-auto max-w-7xl px-6 py-28 lg:py-40">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-14 flex flex-wrap items-end justify-between gap-8"
      >
        <div className="max-w-xl">
          <p className="mono-tech mb-5 text-[#A8853B]">{L({ en: "From the Bench", it: "Dal Banco" })}</p>
          <h2 className="font-display text-4xl leading-tight tracking-tight text-[#0E3B33] sm:text-5xl">
            {L({ en: "Work that", it: "Opere che ci" })} <span className="gold-text italic">{L({ en: "outlives", it: "sopravvivono" })}</span> {L({ en: "us.", it: "" })}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`gallery-filter-${f.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-5 py-2 text-sm transition-all duration-300 ${
                filter === f ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#8A6D2B]" : "border-[#0E3B33]/15 text-[#4A5248] hover:border-[#C9A84C]/60"
              }`}
            >
              {L(CAT_LABELS[f])}
            </button>
          ))}
        </div>
      </motion.div>

      <motion.div layout className="grid auto-rows-[240px] grid-cols-2 gap-4 lg:grid-cols-4">
        <AnimatePresence mode="popLayout">
          {pieces.map((p) => (
            <motion.button
              layout
              key={p.title}
              data-testid={`gallery-item-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setActive(p)}
              className={`group relative overflow-hidden rounded-sm border border-[#0E3B33]/10 shadow-sm ${p.span}`}
            >
              <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E3B33]/85 via-transparent to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-100" />
              <div className="absolute bottom-0 left-0 p-5 text-left">
                <div className="mono-tech text-[#C9A84C]">{L(CAT_LABELS[p.cat])}</div>
                <div className="mt-1 font-display text-xl text-[#FBF9F3]">{p.title}</div>
              </div>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0E3B33]/85 p-6 backdrop-blur-xl"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 10 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-lg border border-[#C9A84C]/40 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.img.replace("w=900", "w=1400")} alt={active.title} className="max-h-[62vh] w-full object-cover" />
              <div className="flex items-center justify-between p-6">
                <div>
                  <div className="mono-tech text-[#A8853B]">{L(CAT_LABELS[active.cat])}</div>
                  <div className="mt-1 font-display text-2xl text-[#0E3B33]">{active.title}</div>
                  <div className="mt-1 text-sm text-[#8A8F82]">{L(active.note)}</div>
                </div>
                <button data-testid="gallery-lightbox-close" onClick={() => setActive(null)} className="rounded-full border border-[#0E3B33]/15 p-2.5 text-[#4A5248] transition-colors hover:border-[#C9A84C] hover:text-[#A8853B]">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
